import {
  createDomainRouterBaseApi,
  type TriedgeDomainRouterBaseApi,
} from "@triedge/rtk-abstractions";
import type {
  AssistantSpreadsheetBridgeResponse,
  CreateMockStaffSettingsFeatureBundleOptions,
  SettingsFeatureBundle,
  SettingsFeatureBundleOptions,
  SettingsNotificationItem,
  SettingsNotificationPreference,
  SettingsRouteManifest,
  SettingsSectionId,
  UpdatePaymentControlPlaneInput,
  UiConsultationRunRecord,
  UiConsultationSessionRecord,
} from "../types";
import {
  applyDomainPatch,
  asRecord,
  parsePayloadRecord,
} from "../utils/settingsState";
import { createMockSettingsAdapters } from "./mockSettingsAdapters";

const TAG_TYPES = [
  "SettingsConfig",
  "SettingsStaffUsers",
  "SettingsProvisioning",
  "SettingsModuleGovernanceAudit",
  "SettingsNotifications",
  "SettingsNotificationPreferences",
  "SettingsPaymentReadiness",
  "SettingsAssistantBridge",
  "SettingsAssistantWorkflow",
  "SettingsUiConsultation",
  "SettingsHistoricalImport",
] as const;

const SETTINGS_SECTIONS: SettingsSectionId[] = [
  "general",
  "integrations",
  "users",
  "admissions",
  "financial-aid",
];

const routeManifestFor = (routeBase: string): SettingsRouteManifest => ({
  routeBase,
  sections: SETTINGS_SECTIONS,
  sectionPath: (section) =>
    `${routeBase}/${section === "financial-aid" ? "financialaid" : section}`,
  defaultPath: `${routeBase}/general`,
});

const asMessage = (error: unknown): { message: string } => ({
  message:
    error instanceof Error
      ? error.message
      : String(error ?? "Unknown settings error"),
});

const withSettingsTags = (baseApi: TriedgeDomainRouterBaseApi) =>
  baseApi.enhanceEndpoints({
    addTagTypes: [...TAG_TYPES],
  });

const settingsConfigTag = (moduleKey: string) => ({
  type: "SettingsConfig" as const,
  id: moduleKey,
});

export const createStaffSettingsFeatureBundle = ({
  routeBase,
  baseApi = createDomainRouterBaseApi({ reducerPath: "settingsFeatureApi" }),
  reducerPath,
  storybookOperatorPersona = "admin",
  resolveOperatorPersona,
  settingsConfigAdapter,
  assistantSpreadsheetBridgeAdapter,
  assistantWorkflowStudioAdapter,
  paymentControlPlaneAdapter,
  financialAidHistoricalImportAdapter,
  uiConsultationAdapter = createMockSettingsAdapters(routeBase)
    .uiConsultationAdapter,
  financialAidRequirementFields = [],
}: SettingsFeatureBundleOptions): SettingsFeatureBundle => {
  const preparedBaseApi =
    reducerPath && baseApi.reducerPath !== reducerPath
      ? createDomainRouterBaseApi({ reducerPath })
      : baseApi;

  const settingsApi = withSettingsTags(preparedBaseApi).injectEndpoints({
    endpoints: (builder: any) => ({
      getStaffSettingsModuleConfig: builder.query({
        queryFn: async (
          args: { orgId?: string | null; moduleKey?: string | null } | void,
        ) => {
          try {
            const data = await settingsConfigAdapter.getModuleConfig(
              args ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (
          _result: unknown,
          _error: unknown,
          args: { moduleKey?: string | null } | void,
        ) => [settingsConfigTag(String(args?.moduleKey ?? "staff_settings"))],
      }),
      updateStaffSettingsModuleConfig: builder.mutation({
        queryFn: async (input: {
          orgId?: string | null;
          moduleKey?: string | null;
          patch: Record<string, unknown>;
          integrationStatus?: Record<string, unknown>;
        }) => {
          try {
            const data = await settingsConfigAdapter.updateModuleConfig(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        async onQueryStarted(
          input: {
            orgId?: string | null;
            moduleKey?: string | null;
            patch: Record<string, unknown>;
            integrationStatus?: Record<string, unknown>;
          },
          { dispatch, queryFulfilled }: any,
        ) {
          const moduleKey = String(input.moduleKey ?? "staff_settings");
          const patchResult = dispatch(
            settingsApi.util.updateQueryData(
              "getStaffSettingsModuleConfig",
              { orgId: input.orgId, moduleKey },
              (draft: unknown) => {
                const record = asRecord(draft);
                const currentPayload = parsePayloadRecord(record.payload);
                record.payload = applyDomainPatch(currentPayload, input.patch);
                record.updatedAt = Date.now();
                record.version = Number(record.version ?? 0) + 1;
              },
            ),
          );
          try {
            const { data } = await queryFulfilled;
            dispatch(
              settingsApi.util.updateQueryData(
                "getStaffSettingsModuleConfig",
                { orgId: input.orgId, moduleKey },
                (draft: unknown) => {
                  Object.assign(asRecord(draft), asRecord(data));
                },
              ),
            );
          } catch {
            patchResult.undo();
          }
        },
        invalidatesTags: (
          _result: unknown,
          _error: unknown,
          input: { moduleKey?: string | null },
        ) => [
          settingsConfigTag(String(input.moduleKey ?? "staff_settings")),
          { type: "SettingsModuleGovernanceAudit", id: "LATEST" },
          { type: "SettingsHistoricalImport", id: "staff_settings" },
        ],
      }),
      listStaffUsers: builder.query({
        queryFn: async (
          args: {
            search?: string;
            statusFilter?: "all" | "active" | "invited" | "deactivated";
            page?: number;
            limit?: number;
          } | void,
        ) => {
          try {
            const data = await settingsConfigAdapter.listStaffUsers(
              args ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsStaffUsers", id: "LIST" }],
      }),
      inviteStaffUser: builder.mutation({
        queryFn: async (input: {
          firstName: string;
          lastName?: string;
          email: string;
          role:
            | "super-administrator"
            | "admin"
            | "principal"
            | "teacher"
            | "nurse"
            | "accounts";
        }) => {
          try {
            const data = await settingsConfigAdapter.inviteStaffUser(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsStaffUsers", id: "LIST" }],
      }),
      resendStaffInvite: builder.mutation({
        queryFn: async (input: { userId: string }) => {
          try {
            const data = await settingsConfigAdapter.resendStaffInvite(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsStaffUsers", id: "LIST" }],
      }),
      updateStaffUserRole: builder.mutation({
        queryFn: async (input: {
          userId: string;
          role:
            | "super-administrator"
            | "admin"
            | "principal"
            | "teacher"
            | "nurse"
            | "accounts";
        }) => {
          try {
            const data = await settingsConfigAdapter.updateStaffUserRole(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsStaffUsers", id: "LIST" }],
      }),
      deactivateStaffUser: builder.mutation({
        queryFn: async (input: { userId: string }) => {
          try {
            const data = await settingsConfigAdapter.deactivateStaffUser(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsStaffUsers", id: "LIST" }],
      }),
      reactivateStaffUser: builder.mutation({
        queryFn: async (input: { userId: string }) => {
          try {
            const data = await settingsConfigAdapter.reactivateStaffUser(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsStaffUsers", id: "LIST" }],
      }),
      listStaffCodes: builder.query({
        queryFn: async () => {
          try {
            const data = await settingsConfigAdapter.listStaffCodes();
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsProvisioning", id: "STAFF_CODES" }],
      }),
      createStaffCode: builder.mutation({
        queryFn: async (input: { label: string }) => {
          try {
            const data = await settingsConfigAdapter.createStaffCode(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsProvisioning", id: "STAFF_CODES" }],
      }),
      rotateStaffCode: builder.mutation({
        queryFn: async (input: { codeId: string }) => {
          try {
            const data = await settingsConfigAdapter.rotateStaffCode(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsProvisioning", id: "STAFF_CODES" }],
      }),
      revokeStaffCode: builder.mutation({
        queryFn: async (input: { codeId: string }) => {
          try {
            const data = await settingsConfigAdapter.revokeStaffCode(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsProvisioning", id: "STAFF_CODES" }],
      }),
      listApprovedDomains: builder.query({
        queryFn: async () => {
          try {
            const data = await settingsConfigAdapter.listApprovedDomains();
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [
          { type: "SettingsProvisioning", id: "APPROVED_DOMAINS" },
        ],
      }),
      addApprovedDomain: builder.mutation({
        queryFn: async (input: { domain: string }) => {
          try {
            const data = await settingsConfigAdapter.addApprovedDomain(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsProvisioning", id: "APPROVED_DOMAINS" },
        ],
      }),
      removeApprovedDomain: builder.mutation({
        queryFn: async (input: { domain: string }) => {
          try {
            const data =
              await settingsConfigAdapter.removeApprovedDomain(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsProvisioning", id: "APPROVED_DOMAINS" },
        ],
      }),
      listModuleEnablementAuditEvents: builder.query({
        queryFn: async (
          args: { schoolId?: string | null; limit?: number } | void,
        ) => {
          try {
            const data =
              await settingsConfigAdapter.listModuleEnablementAuditEvents(
                args ?? undefined,
              );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsModuleGovernanceAudit", id: "LATEST" }],
      }),
      listStaffSettingsNotifications: builder.query({
        queryFn: async (args: Record<string, unknown> | void) => {
          try {
            const data = settingsConfigAdapter.listNotifications
              ? await settingsConfigAdapter.listNotifications(args ?? undefined)
              : ([] as SettingsNotificationItem[]);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsNotifications", id: "LIST" }],
      }),
      listStaffSettingsNotificationPreferences: builder.query({
        queryFn: async (args: Record<string, unknown> | void) => {
          try {
            const data = settingsConfigAdapter.listNotificationPreferences
              ? await settingsConfigAdapter.listNotificationPreferences(
                  args ?? undefined,
                )
              : ([] as SettingsNotificationPreference[]);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsNotificationPreferences", id: "LIST" }],
      }),
      updateStaffSettingsNotificationPreferences: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = settingsConfigAdapter.updateNotificationPreferences
              ? await settingsConfigAdapter.updateNotificationPreferences(input)
              : input;
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsNotificationPreferences", id: "LIST" },
        ],
      }),
      getPaymentControlPlaneReadiness: builder.query({
        queryFn: async (args: { tenantId?: string | null } | void) => {
          try {
            const data = await paymentControlPlaneAdapter.getReadiness(
              args ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsPaymentReadiness", id: "READINESS" }],
      }),
      updatePaymentControlPlane: builder.mutation({
        queryFn: async (input: UpdatePaymentControlPlaneInput) => {
          try {
            const data = await paymentControlPlaneAdapter.updateControlPlane(
              input as any,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsPaymentReadiness", id: "READINESS" },
        ],
      }),
      startStripeConnectedAccountOnboarding: builder.mutation({
        queryFn: async (input: {
          tenantId?: string | null;
          existingStripeAccountId?: string | null;
          contactEmail?: string | null;
          country?: string | null;
          businessType?: "company" | "individual";
          refreshUrl: string;
          returnUrl: string;
        }) => {
          try {
            const data =
              await paymentControlPlaneAdapter.startConnectedAccountOnboarding(
                input,
              );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsPaymentReadiness", id: "READINESS" },
        ],
      }),
      completeStripeConnectedAccountOnboarding: builder.mutation({
        queryFn: async (input: {
          tenantId?: string | null;
          stripeAccountId?: string | null;
        }) => {
          try {
            const data =
              await paymentControlPlaneAdapter.completeConnectedAccountOnboarding(
                input,
              );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsPaymentReadiness", id: "READINESS" },
        ],
      }),
      getAssistantSpreadsheetBridgeStatus: builder.query({
        queryFn: async (
          args: { tenantId?: string | null; userSub?: string | null } | void,
        ) => {
          try {
            const data = await assistantSpreadsheetBridgeAdapter.getStatus(
              args ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      startAssistantSpreadsheetBridgeAuth: builder.mutation({
        queryFn: async (input: {
          tenantId?: string | null;
          userSub?: string | null;
          redirectUri?: string | null;
          requestedScopes?: string[];
        }) => {
          try {
            const data =
              await assistantSpreadsheetBridgeAdapter.startAuth(input);
            return { data: data as AssistantSpreadsheetBridgeResponse };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      completeAssistantSpreadsheetBridgeAuth: builder.mutation({
        queryFn: async (input: { state: string; code: string }) => {
          try {
            const data =
              await assistantSpreadsheetBridgeAdapter.completeAuth(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      exportAssistantSpreadsheetToGoogleDrive: builder.mutation({
        queryFn: async (input: {
          tenantId?: string | null;
          userSub?: string | null;
          destinationFolderId?: string | null;
          destinationFileId?: string | null;
          fileName: string;
          workbookArtifactPath?: string | null;
          artifactDownloadUrl?: string | null;
          mimeType?: string | null;
        }) => {
          try {
            const data =
              await assistantSpreadsheetBridgeAdapter.exportToDrive(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      disconnectAssistantSpreadsheetBridge: builder.mutation({
        queryFn: async (
          input: { tenantId?: string | null; userSub?: string | null } | void,
        ) => {
          try {
            const data = await assistantSpreadsheetBridgeAdapter.disconnect(
              input ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      saveAssistantSpreadsheetExportPreset: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data =
              await assistantSpreadsheetBridgeAdapter.savePreset(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      deleteAssistantSpreadsheetExportPreset: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data =
              await assistantSpreadsheetBridgeAdapter.deletePreset(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: "SettingsAssistantBridge", id: "STATUS" }],
      }),
      getAssistantWorkflowStudioState: builder.query({
        queryFn: async (input: {
          workflowId: string;
          pathname?: string | null;
        }) => {
          try {
            const data =
              await assistantWorkflowStudioAdapter.getStudioState(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (
          _result: unknown,
          _error: unknown,
          input: { workflowId: string },
        ) => [
          {
            type: "SettingsAssistantWorkflow",
            id: String(input.workflowId ?? "new"),
          },
        ],
      }),
      upsertAssistantWorkflowDraft: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = await assistantWorkflowStudioAdapter.upsertDraft(
              input as any,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (
          _result: unknown,
          _error: unknown,
          input: { workflowId?: string | null },
        ) => [
          {
            type: "SettingsAssistantWorkflow",
            id: String(input.workflowId ?? "new"),
          },
        ],
      }),
      submitAssistantWorkflowForApproval: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = await assistantWorkflowStudioAdapter.submitForApproval(
              input as any,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (
          _result: unknown,
          _error: unknown,
          input: { workflowDefinitionId?: string | null },
        ) => [
          {
            type: "SettingsAssistantWorkflow",
            id: String(input.workflowDefinitionId ?? "new"),
          },
        ],
      }),
      reviewAssistantWorkflow: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = await assistantWorkflowStudioAdapter.reviewWorkflow(
              input as any,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (
          _result: unknown,
          _error: unknown,
          input: { workflowDefinitionId?: string | null },
        ) => [
          {
            type: "SettingsAssistantWorkflow",
            id: String(input.workflowDefinitionId ?? "new"),
          },
        ],
      }),
      mutateAssistantRun: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = await assistantWorkflowStudioAdapter.mutateRun(
              input as any,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (
          _result: unknown,
          _error: unknown,
          input: {
            context?: {
              assistantTask?: { workflowDefinitionId?: string | null };
            };
          },
        ) => [
          {
            type: "SettingsAssistantWorkflow",
            id: String(
              input.context?.assistantTask?.workflowDefinitionId ?? "new",
            ),
          },
        ],
      }),
      getUiConsultationServiceStatus: builder.query({
        queryFn: async () => {
          try {
            const data = await uiConsultationAdapter.getServiceStatus();
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsUiConsultation", id: "STATUS" }],
      }),
      listUiConsultationSessions: builder.query({
        queryFn: async () => {
          try {
            const data = await uiConsultationAdapter.listSessions();
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [{ type: "SettingsUiConsultation", id: "SESSIONS" }],
      }),
      getUiConsultationSession: builder.query({
        queryFn: async (input: { sessionId: string }) => {
          try {
            const data = await uiConsultationAdapter.getSession(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (
          _result: UiConsultationSessionRecord | null,
          _error: unknown,
          input: { sessionId: string },
        ) => [
          { type: "SettingsUiConsultation", id: `SESSION:${input.sessionId}` },
        ],
      }),
      saveUiConsultationSession: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = await uiConsultationAdapter.saveSession(input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: UiConsultationSessionRecord | undefined) => [
          { type: "SettingsUiConsultation", id: "SESSIONS" },
        ],
      }),
      listUiConsultationRuns: builder.query({
        queryFn: async (input: { sessionId: string }) => {
          try {
            const data = await uiConsultationAdapter.listRuns(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (
          _result: UiConsultationRunRecord[],
          _error: unknown,
          input: { sessionId: string },
        ) => [
          { type: "SettingsUiConsultation", id: `RUNS:${input.sessionId}` },
        ],
      }),
      runUiConsultation: builder.mutation({
        queryFn: async (input: Record<string, unknown>) => {
          try {
            const data = await uiConsultationAdapter.runConsultation(
              input as any,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (
          _result: UiConsultationRunRecord | undefined,
          _error: unknown,
          input: { session?: { id?: string | null } },
        ) => [
          { type: "SettingsUiConsultation", id: "SESSIONS" },
          {
            type: "SettingsUiConsultation",
            id: `RUNS:${String(input.session?.id ?? "new")}`,
          },
          { type: "SettingsUiConsultation", id: "STATUS" },
        ],
      }),
      getFinancialAidHistoricalImportTemplate: builder.query({
        queryFn: async (
          args: { orgId?: string | null; moduleKey?: string | null } | void,
        ) => {
          try {
            const data = await financialAidHistoricalImportAdapter.getTemplate(
              args ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [
          { type: "SettingsHistoricalImport", id: "staff_settings" },
        ],
      }),
      getFinancialAidHistoricalImportStatus: builder.query({
        queryFn: async (
          args: {
            orgId?: string | null;
            moduleKey?: string | null;
            importId?: string | null;
          } | void,
        ) => {
          try {
            const data = await financialAidHistoricalImportAdapter.getStatus(
              args ?? undefined,
            );
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: [
          { type: "SettingsHistoricalImport", id: "staff_settings" },
        ],
      }),
      requestFinancialAidHistoricalImportUpload: builder.mutation({
        queryFn: async (input: {
          orgId?: string | null;
          moduleKey?: string | null;
          fileName: string;
          fileType: string;
          fileSizeBytes: number;
        }) => {
          try {
            const data =
              await financialAidHistoricalImportAdapter.requestUpload(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsHistoricalImport", id: "staff_settings" },
        ],
      }),
      startFinancialAidHistoricalImport: builder.mutation({
        queryFn: async (input: {
          orgId?: string | null;
          moduleKey?: string | null;
          importId?: string | null;
        }) => {
          try {
            const data =
              await financialAidHistoricalImportAdapter.startImport(input);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [
          { type: "SettingsHistoricalImport", id: "staff_settings" },
        ],
      }),
    }),
  });

  const manifest = routeManifestFor(routeBase);

  return {
    id: "settings",
    api: settingsApi,
    hooks: {
      useGetStaffSettingsModuleConfigQuery:
        settingsApi.useGetStaffSettingsModuleConfigQuery,
      useUpdateStaffSettingsModuleConfigMutation:
        settingsApi.useUpdateStaffSettingsModuleConfigMutation,
      useListStaffUsersQuery: settingsApi.useListStaffUsersQuery,
      useInviteStaffUserMutation: settingsApi.useInviteStaffUserMutation,
      useResendStaffInviteMutation: settingsApi.useResendStaffInviteMutation,
      useUpdateStaffUserRoleMutation:
        settingsApi.useUpdateStaffUserRoleMutation,
      useDeactivateStaffUserMutation:
        settingsApi.useDeactivateStaffUserMutation,
      useReactivateStaffUserMutation:
        settingsApi.useReactivateStaffUserMutation,
      useListStaffCodesQuery: settingsApi.useListStaffCodesQuery,
      useCreateStaffCodeMutation: settingsApi.useCreateStaffCodeMutation,
      useRotateStaffCodeMutation: settingsApi.useRotateStaffCodeMutation,
      useRevokeStaffCodeMutation: settingsApi.useRevokeStaffCodeMutation,
      useListApprovedDomainsQuery: settingsApi.useListApprovedDomainsQuery,
      useAddApprovedDomainMutation: settingsApi.useAddApprovedDomainMutation,
      useRemoveApprovedDomainMutation:
        settingsApi.useRemoveApprovedDomainMutation,
      useListModuleEnablementAuditEventsQuery:
        settingsApi.useListModuleEnablementAuditEventsQuery,
      useListStaffSettingsNotificationsQuery:
        settingsApi.useListStaffSettingsNotificationsQuery,
      useListStaffSettingsNotificationPreferencesQuery:
        settingsApi.useListStaffSettingsNotificationPreferencesQuery,
      useUpdateStaffSettingsNotificationPreferencesMutation:
        settingsApi.useUpdateStaffSettingsNotificationPreferencesMutation,
      useGetPaymentControlPlaneReadinessQuery:
        settingsApi.useGetPaymentControlPlaneReadinessQuery,
      useUpdatePaymentControlPlaneMutation:
        settingsApi.useUpdatePaymentControlPlaneMutation,
      useStartStripeConnectedAccountOnboardingMutation:
        settingsApi.useStartStripeConnectedAccountOnboardingMutation,
      useCompleteStripeConnectedAccountOnboardingMutation:
        settingsApi.useCompleteStripeConnectedAccountOnboardingMutation,
      useGetAssistantSpreadsheetBridgeStatusQuery:
        settingsApi.useGetAssistantSpreadsheetBridgeStatusQuery,
      useStartAssistantSpreadsheetBridgeAuthMutation:
        settingsApi.useStartAssistantSpreadsheetBridgeAuthMutation,
      useCompleteAssistantSpreadsheetBridgeAuthMutation:
        settingsApi.useCompleteAssistantSpreadsheetBridgeAuthMutation,
      useExportAssistantSpreadsheetToGoogleDriveMutation:
        settingsApi.useExportAssistantSpreadsheetToGoogleDriveMutation,
      useDisconnectAssistantSpreadsheetBridgeMutation:
        settingsApi.useDisconnectAssistantSpreadsheetBridgeMutation,
      useSaveAssistantSpreadsheetExportPresetMutation:
        settingsApi.useSaveAssistantSpreadsheetExportPresetMutation,
      useDeleteAssistantSpreadsheetExportPresetMutation:
        settingsApi.useDeleteAssistantSpreadsheetExportPresetMutation,
      useGetAssistantWorkflowStudioStateQuery:
        settingsApi.useGetAssistantWorkflowStudioStateQuery,
      useUpsertAssistantWorkflowDraftMutation:
        settingsApi.useUpsertAssistantWorkflowDraftMutation,
      useSubmitAssistantWorkflowForApprovalMutation:
        settingsApi.useSubmitAssistantWorkflowForApprovalMutation,
      useReviewAssistantWorkflowMutation:
        settingsApi.useReviewAssistantWorkflowMutation,
      useMutateAssistantRunMutation: settingsApi.useMutateAssistantRunMutation,
      useGetUiConsultationServiceStatusQuery:
        settingsApi.useGetUiConsultationServiceStatusQuery,
      useListUiConsultationSessionsQuery:
        settingsApi.useListUiConsultationSessionsQuery,
      useGetUiConsultationSessionQuery:
        settingsApi.useGetUiConsultationSessionQuery,
      useSaveUiConsultationSessionMutation:
        settingsApi.useSaveUiConsultationSessionMutation,
      useListUiConsultationRunsQuery:
        settingsApi.useListUiConsultationRunsQuery,
      useRunUiConsultationMutation: settingsApi.useRunUiConsultationMutation,
      useLazyGetFinancialAidHistoricalImportTemplateQuery:
        settingsApi.useLazyGetFinancialAidHistoricalImportTemplateQuery,
      useGetFinancialAidHistoricalImportStatusQuery:
        settingsApi.useGetFinancialAidHistoricalImportStatusQuery,
      useRequestFinancialAidHistoricalImportUploadMutation:
        settingsApi.useRequestFinancialAidHistoricalImportUploadMutation,
      useStartFinancialAidHistoricalImportMutation:
        settingsApi.useStartFinancialAidHistoricalImportMutation,
    },
    storybookOperatorPersona,
    resolveOperatorPersona,
    routeManifest: manifest,
    pageRegistry: {
      general: "StaffSettingsPage",
      integrations: "StaffSettingsPage",
      users: "StaffSettingsPage",
      admissions: "StaffSettingsPage",
      "financial-aid": "StaffSettingsPage",
    },
    storybookFixtures: {
      title: "Settings Feature Bundle",
      routeBase,
      sections: [...SETTINGS_SECTIONS],
    },
    financialAidRequirementFields,
    adapters: {
      settingsConfigAdapter,
      assistantSpreadsheetBridgeAdapter,
      assistantWorkflowStudioAdapter,
      paymentControlPlaneAdapter,
      financialAidHistoricalImportAdapter,
      uiConsultationAdapter,
    },
  };
};

export const createMockStaffSettingsFeatureBundle = (
  routeBase = "/school/settings",
  options: CreateMockStaffSettingsFeatureBundleOptions = {},
) => {
  const adapters = createMockSettingsAdapters(routeBase);
  return createStaffSettingsFeatureBundle({
    routeBase,
    reducerPath: "mockSettingsFeatureApi",
    storybookOperatorPersona: options.operatorPersona ?? "admin",
    financialAidRequirementFields: options.financialAidRequirementFields ?? [],
    ...adapters,
  });
};

export { createMockSettingsAdapters };
